import React, { useEffect, useState, useRef } from "react";

import {
  Avatar,
  Button,
  Col,
  Row,
  Spin,
  Tag,
  Timeline,
  Typography,
} from "antd";
import { CircleCheck, RefreshCcw, UserRound } from "lucide-react";

import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);
import moment from "moment";
import { translationsTypes } from "../planning/columns-table";
const { Title, Paragraph, Text } = Typography;

const dateFormat = "DD/MM/YYYY";
import { FONT } from "constants/index";
import FormLoadUserComparation from "../load-control/load-control-drawer-comparation";
import DrawerApp from "components/drawer-app";
import {
  getPhasesAquatic,
  getTraining,
} from "services/aquatic/aquatic-service";

const TimelineAqua = (props) => {
  const [loadingData, setLoadingData] = useState(false);
  const [imgAvatar, setImgAvatar] = useState("");
  const [trainings, setTrainings] = useState([]);
  const [phases, setPhases] = useState([{ subphases: [] }]);

  useEffect(() => {
    if (props.user?.id) {
      getUserTraining();
    }
  }, [phases]);

  useEffect(() => {
    setImgAvatar(props.user?.img);
    getPhases();
  }, [props]);

  async function getUserTraining() {
    setLoadingData(true);

    try {
      const data = await getTraining(props.user?.id);

      const timelineData = buildTimeline(
        data.allTrainings,
        data.tasksDone,
        phases
      );

      setTrainings(timelineData);
    } catch (error) {
      notificationErroInternal(
        "Aconteceu um erro inesperado, por favor, tente mais tarde."
      );
    } finally {
      setLoadingData(false);
    }
  }

  const getPhases = async () => {
    setLoadingData(true);
    try {
      const data = await getPhasesAquatic();
      setPhases(data);
    } catch (error) {
      notificationErroInternal(
        "Aconteceu um erro inesperado, por favor, tente mais tarde."
      );
    } finally {
      setLoadingData(false);
    }
  };

  function buildTimeline(allTrainings, tasksDone, phases) {
    return allTrainings.map((training) => {
      const subphase = findSubphaseById(phases, training.subphasesId);
      const objectives = subphase?.objectives ?? [];

      const trainingDate = new Date(training.updatedAt || training.createdAt);

      const tasksDoneUntilTraining = tasksDone.filter(
        (task) => new Date(task.createdAt) <= trainingDate
      );

      const tasksDoneMap = new Map(
        tasksDoneUntilTraining.map((task) => [task.taskId, task])
      );

      return {
        id: training.id,
        date: trainingDate,
        isDone: training.isDone,
        annotation: training.annotation,
        subphaseName: subphase?.name || "Fase desconhecida",
        objectives: objectives.map((objective) => {
          const tasks = objective.tasks ?? [];

          const executedTasks = tasks.filter((task) =>
            tasksDoneMap.has(task.id)
          );

          const allTasksExecuted =
            tasks.length > 0 && executedTasks.length === tasks.length;

          let nextTask = null;

          if (!allTasksExecuted) {
            const highest =
              executedTasks.length > 0
                ? executedTasks.reduce((max, task) =>
                    task.order > max.order ? task : max
                  ).order + 1
                : 1;

            nextTask = tasks.find((t) => t.order === highest);
          }

          return {
            name: objective.name,
            allTasksExecuted,
            nextTask,
            total: tasks.length,
            done: executedTasks.length,
          };
        }),
      };
    });
  }
  function findSubphaseById(phases, subphaseId) {
    for (const phase of phases) {
      const found = phase.subphases.find((sub) => sub.id === subphaseId);
      if (found) return found;
    }
    return null;
  }

  return (
    <>
      <Spin spinning={loadingData}>
        <Row gutter={16} style={{ textAlign: "center", marginBottom: "30px" }}>
          <Col span={24}>
            <Avatar
              src={imgAvatar}
              icon={<UserRound size={120} />}
              size={120}
            />
          </Col>
        </Row>
        <Row gutter={16} style={{ textAlign: "center", marginBottom: "30px" }}>
          <Col span={24}>
            <Text strong style={{ fontFamily: FONT }}>
              {props.user?.name}
            </Text>
          </Col>
        </Row>

        <Timeline mode="alternate" reverse style={{ fontFamily: FONT }}>
          {trainings.map((item, indexTraining) => (
            <Timeline.Item
              position={indexTraining % 2 === 0 ? "left" : "right"}
              key={item.id}
              color={item.isDone ? "green" : "blue"}
              dot={
                item.isDone ? (
                  <CircleCheck size={16} />
                ) : (
                  <RefreshCcw size={16} />
                )
              }
            >
              <div style={{ fontWeight: 500 }}>
                {item.isDone
                  ? `Treino dia ${dayjs(item.date).format(dateFormat)}`
                  : "Treino Atual"}
              </div>

              <div
                style={{
                  marginTop: 12,
                  paddingLeft: 8,
                }}
              >
                <div
                  style={{
                    alignItems: "center",
                    display: "flex",
                  }}
                >
                  <Title level={5} style={{ marginBottom: 16 }}>
                    {item.subphaseName}
                  </Title>
                </div>

                {item.objectives.map((objective, index) => (
                  <div key={index} style={{ marginBottom: 20 }}>
                    <Text strong>{objective.name}</Text>

                    {objective.allTasksExecuted ? (
                      <Text
                        style={{
                          display: "block",
                          marginTop: 6,
                          color: "#09ac9c",
                          fontSize: 13,
                        }}
                      >
                        Todas as tarefas concluídas!
                      </Text>
                    ) : (
                      <>
                        <Text
                          style={{
                            display: "block",
                            marginTop: 6,
                            fontSize: 13,
                          }}
                        >
                          Próxima: {objective.nextTask?.name}
                        </Text>

                        <Text
                          style={{
                            fontSize: 12,
                            color: "#888",
                          }}
                        >
                          {objective.done}/{objective.total} concluídas
                        </Text>
                      </>
                    )}
                  </div>
                ))}
                {item.annotation && (
                  <>
                    <Title level={5} style={{ marginTop: 12 }}>
                      Anotações
                    </Title>
                    <Text>{item.annotation}</Text>
                  </>
                )}
              </div>
            </Timeline.Item>
          ))}
        </Timeline>
      </Spin>
    </>
  );
};
export default TimelineAqua;
